<template>
  <div>
    <RevLoadingScreen
      v-if="isSubmitting"
      class="mt-20 size-full self-center"
      :text="i18n(translations.updatingText)"
      variant="light"
    />
    <template v-if="!isSubmitting">
      <RevInfoBlock
        class="mb-24"
        :content="i18n(translations.modalInfoBlockContent)"
        :icon="IconLightbulb"
        :title="i18n(translations.modalInfoBlockTitle)"
        variant="warning"
      />
      <RevInfoBlock
        v-if="apiError"
        class="mb-4"
        :icon="IconWarning"
        :title="i18n(translations.errorTitle)"
        variant="danger"
      >
        {{ apiError }}
      </RevInfoBlock>
      <RevForm :id="FORM_ID" :initial-values :validate @submit="handleSubmit">
        <template #default="{ values, errors }">
          <div class="mb-16">
            <RevInputText
              id="legalCompanyName"
              v-model="values.legalCompanyName"
              class="mb-12"
              :error="errors.legalCompanyName"
              :label="i18n(translations.kanjiLegalCompanyName)"
              type="text"
            />
            <RevInputText
              id="kanaLegalCompanyName"
              v-model="values.kanaLegalCompanyName"
              class="mb-12"
              :error="errors.kanaLegalCompanyName"
              :label="i18n(translations.kanaLegalCompanyName)"
              type="text"
            />
            <RevInputText
              id="latinLegalCompanyName"
              v-model="values.latinLegalCompanyName"
              class="mb-12"
              :error="errors.latinLegalCompanyName"
              :label="i18n(translations.latinLegalCompanyName)"
              type="text"
            />
            <RevInputSelect
              id="organizationType"
              v-model="values.organizationType"
              class="mb-12"
              data-cs-mask
              :error="errors.organizationType"
              :label="i18n(translations.organizationType)"
              name="organizationType"
              :options="organizationTypeOptions"
            />
            <RevInputText
              id="companyIdentificationNumber"
              v-model="values.companyIdentificationNumber"
              class="mb-12"
              :description="companyIdNumberInputDescription"
              :disabled="shouldDisableCompanyIdNumberInput"
              :error="errors.companyIdentificationNumber"
              :label="i18n(translations.companyIdentificationNumberInputLabel)"
              type="text"
            />
            <RevInputText
              id="secondhandDealerNumber"
              v-model="values.secondhandDealerNumber"
              class="mb-12"
              :error="errors.secondhandDealerNumber"
              :label="i18n(translations.secondhandDealerNumber)"
              type="text"
            />
            <RevInputText
              id="secondhandDealerNumberIssuingPrefecture"
              v-model="values.secondhandDealerNumberIssuingPrefecture"
              class="mb-12"
              :error="errors.secondhandDealerNumberIssuingPrefecture"
              :label="
                i18n(translations.secondhandDealerNumberIssuingPrefecture)
              "
              type="text"
            />
          </div>
        </template>
      </RevForm>
    </template>
  </div>
</template>

<script setup lang="ts">
import { type Error, getApiErrorMessage } from '#imports'
import { computed, ref } from 'vue'

import { sellerProfileAPI } from '@backmarket/http-api'
import type {
  GetLegalDetailsMetadata,
  GetProfileViewLegalDetails,
} from '@backmarket/http-api/src/api-specs-seller-backoffice/seller-profile'
import { $httpFetch } from '@backmarket/nuxt-module-http/$httpFetch'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { useTheToast } from '@backmarket/nuxt-module-toast/useTheToast'
import {
  RevForm,
  makeValidate,
  matchingRegExp,
  maxLength,
  required,
} from '@ds/components/Form'
import { RevInfoBlock } from '@ds/components/InfoBlock'
import { RevInputSelect } from '@ds/components/InputSelect'
import { RevInputText } from '@ds/components/InputText'
import { RevLoadingScreen } from '@ds/components/LoadingScreen'
import { IconLightbulb } from '@ds/icons/IconLightbulb'
import { IconWarning } from '@ds/icons/IconWarning'

import { FORM_IDS } from '~/scopes/profile/pages/Profile.constants'

import translations from '../../OverviewModal.translations'

import {
  MAX_LEGAL_COMPANY_NAME_LENGTH,
  VALID_JAPAN_IDENTIFICATION_NUMBER_REGEXP,
  VALID_JAPAN_SECONDHAND_NUMBER_REGEXP,
} from './JapanForm.constants'
import type { FormSellerLegalDetailsValues } from './JapanForm.types'

const FORM_ID = FORM_IDS.EDIT_LEGAL_DETAILS.japan

const props = defineProps<{
  legalDetails: GetProfileViewLegalDetails | null
  legalDetailsMetadata: GetLegalDetailsMetadata | null
}>()

const emit = defineEmits(['update-form', 'close-modal'])

const i18n = useI18n()
const { openSuccessToast } = useTheToast()

const apiError = ref('')
const isSubmitting = ref(false)

const initialValues = computed<FormSellerLegalDetailsValues>(() => ({
  legalCompanyName: '',
  kanaLegalCompanyName: '',
  latinLegalCompanyName: '',
  companyIdentificationNumber: '',
  secondhandDealerNumber: '',
  secondhandDealerNumberIssuingPrefecture: '',
  ...props.legalDetails,
  organizationType: props.legalDetails?.organizationType?.code || '',
}))

const shouldDisableCompanyIdNumberInput = computed(
  () => !!props.legalDetails?.companyIdentificationNumber,
)

const companyIdNumberInputDescription = computed(() =>
  shouldDisableCompanyIdNumberInput.value
    ? i18n(translations.labelDisabledInput)
    : '',
)

const organizationTypeOptions = computed(() =>
  (props.legalDetailsMetadata?.organizationTypes || []).map(
    ({ label, code }) => ({
      label: label || '',
      value: code,
    }),
  ),
)

const validate = makeValidate<FormSellerLegalDetailsValues>({
  legalCompanyName: [
    required(i18n(translations.required)),
    maxLength(MAX_LEGAL_COMPANY_NAME_LENGTH, i18n(translations.invalidLength)),
  ],
  kanaLegalCompanyName: [
    // TODO Missing validators
  ],
  latinLegalCompanyName: [
    // TODO Missing validators
  ],
  companyIdentificationNumber: [
    matchingRegExp(
      VALID_JAPAN_IDENTIFICATION_NUMBER_REGEXP,
      i18n(translations.invalidAustraliaIdentificationNumber),
    ),
  ],
  secondhandDealerNumber: [
    matchingRegExp(
      VALID_JAPAN_SECONDHAND_NUMBER_REGEXP,
      i18n(translations.invalidJapanSecondhandNumber),
    ),
  ],
  secondhandDealerNumberIssuingPrefecture: [
    // TODO it's not supposed to be required
    // required(i18n(translations.required)),
  ],
  organizationType: [
    // TODO Missing validators
  ],
})

async function handleSubmit({
  companyIdentificationNumber,
  legalCompanyName,
  kanaLegalCompanyName,
  latinLegalCompanyName,
  secondhandDealerNumber,
  secondhandDealerNumberIssuingPrefecture,
  organizationType,
}: FormSellerLegalDetailsValues) {
  apiError.value = ''
  isSubmitting.value = true
  try {
    await $httpFetch(sellerProfileAPI.updateProfile, {
      body: {
        legalCompanyName,
        kanaLegalCompanyName,
        latinLegalCompanyName,
        ...(!shouldDisableCompanyIdNumberInput.value &&
        companyIdentificationNumber
          ? { companyIdentificationNumber }
          : null),
        secondhandDealerNumber,
        secondhandDealerNumberIssuingPrefecture,
        organizationType,
      },
    })
    openSuccessToast({
      title: i18n(translations.legalDetailsUpdatedToastSuccessTitle),
      content: i18n(translations.legalDetailsUpdatedToastSuccessContent),
    })
    emit('update-form')
    emit('close-modal')
  } catch (error) {
    apiError.value = getApiErrorMessage(error as unknown as Error)
  } finally {
    isSubmitting.value = false
  }
}
</script>
